import React from 'react'
import {
  Typography,
  Box,
  Collapse,
  Grid,
  CircularProgress,
  Grow,
  Alert,
  BoxProps,
} from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import {
  useParams,
  useLocation,
  matchRoutes,
  useNavigate,
} from 'react-router-dom'
import { useForm, Path, useWatch } from 'react-hook-form'
import { TransitionGroup } from 'react-transition-group'
import { usePropertyAd } from 'api/services/PropertyAd'
import { IStep } from './types'
import { useAuthStore, useCreatePropertyAdStore } from 'store'
import Step1 from './Step1'
import StepPropertyLocation from './StepPropertyLocation'
import Step3 from './StepProperty'
import StepAdAttributes from './StepAdAttributes'
import StepPhotos from './StepPhotos'
import Controls from './Controls'
import VerticalStepper from './Stepper'
import LinearProgressWithLabel from './Progress'
import shallow from 'zustand/shallow'
import NotFound from 'components/NotFound'
import { parseAdminPropertyAd, ParsedAdminPropertyAd } from './helpers'
import { PROPERTY_AD_STATUS } from 'helpers/constants'
import { FilePathWithPreview } from 'store/CreatePropertyAdSlice/types'
import {
  I18nText,
  I18nAddress,
  SearchLocation,
  AdminPropertyAd,
  EstateAgent,
  PropertyAdStatus,
  ValidationMessage,
} from 'types'
import { useMutation } from 'react-query'
import { getAgent } from 'api/services/Agent'
import { styled } from '@mui/material/styles'
import { omit } from 'lodash'
import usePageView from 'hooks/usePageView'
import { PublishingStatusEnum } from 'types/enums'
import KeycloakService from 'services/KeycloakService'

const StyledBox = styled((props) => (
  <Box
    sx={{ display: 'grid', gridTemplateRows: '1fr', gap: '10px' }}
    {...props}
  />
))<BoxProps>(({ theme }) => ({}))

export interface FormValues {
  step1: {
    category: string
    subCategory: string
    interest: string
  }
  location: {
    searchAreaLocationId: string
    searchLocation: SearchLocation | null
    addresses: I18nAddress[]
    x: number | null
    y: number | null
    isPublic: boolean
  }
  property: {
    extras: {
      alarm: string
      safe: string
      fireplace: string
      furnished: string
      solarWaterHeater: string
      professionalUse: string
      penthouse: string
      doubleFrontage: string
      withLight: string
      withAir: string
      underFloorHeat: string
      maintained: string
      screens: string
      cctv: string
      disabledAccess: string
      facade: string
      corner: string
      airCondition: string
      pets: string
      slopeAmphitheater: string
      compensation: string
      waterSupply: string
      electrification: string
      enclosure: string
      drains: string
      islandBuildable: string
      marina: string
      structuredWiring: string
      falseCeiling: string
      fireSafety: string
      kitchen: string
      doorman: string
      internalStairs: string
      foodService: string
      securityRolls: string
      cargoRamp: string
      guarding: string
      carElevator: string
      gasStation: string
      carWash: string
      waterHeating: string
      spa: string
      roofGarden: string
      facilities: string
      eventSpace: string
      intercom: string
      cellar: string
      outBar: string
      staffRooms: string
      organizedBeach: string
      meetingRoom: string
      renovation: string
      balcony: string
      tents: string
      garden: string
      loft: string
      storage: string
      withBuilding: string
      maintenanceFees: string
      nonForest: string
      restaurant: string
      expandability: string
      equipped: string
      commercialPlot: string
      cargoElevator: string
    }
    // salesPrice: number | string
    squareMeters: number | string
    constructionYear: number | string
    floors: string[]
    heat: string
    heatType: string[]
    estateCondition: string
    door: string
    energyClass: string
    frame: string
    windows: string
    floor: string
    access: string
    garden: string
    direction: string
    view: string
    pool: string[]
    usage: string
    plan: string
    landCondition: string[]
    electricity: string
    bedrooms: number | string
    masterBedrooms: number | string
    livingRooms: number | string
    baths: number | string
    wc: number | string
    buildFactor: number | string
    coverageFactor: number | string
    maxAllowedHeight: number | string
    plotWidth: number | string
    plotLength: number | string
    distancePort: number | string
    distanceRoadNetwork: number | string
    distanceVillage: number | string
    distanceTown: number | string
    distanceSea: number | string
    distanceAirport: number | string
    numberOfViews: number | string
    beaches: number | string
    islandPerimeter: number | string
    spaces: number | string
    equipment: string
    maxInnerHeight: number | string
    industrialDoorHeight: number | string
    industrialDoorWidth: number | string
    entrances: number | string
    offices: number | string
    entranceHeight: number | string
    parking: string
    slotsCar: number | string
    slotsMotorcycle: number | string
    slotsBoat: number | string
    rooms: number | string
    gyms: number | string
    //
    renovationYear: number | string
    balconySqr: number | string
    gardenSqr: number | string
    loftSqr: number | string
    storageSqr: number | string
    maintenanceFeesValue: number | string
    parkingSlots: number | string
    withBuildingSqr: number | string
    forestCover: number | string
    forestCoverSqr: number | string
    numberOfRestaurants: number | string
    expandabilitySqr: number | string
    plotSqr: number | string
    cargoElevatorLoad: number | string
  }
  adPhotos: {
    photos: FilePathWithPreview[]
  }
  adAttributes: {
    estateCode: string
    status: PropertyAdStatus | ''
    salesPrice: number | string
    descriptions: I18nText[]
    titles: I18nText[]
    interest: string
    agentCInfo: string | number
  }
}

export const DefaultValues = {
  step1: {
    category: '',
    subCategory: '',
  },
  location: {
    searchAreaLocationId: '',
    searchLocation: null,
    addresses: [
      {
        addressDescription: '',
        street: '',
        streetNumber: '',
        postalCode: '',
        city: '',
        area: '',
        country: 'Ελλάδα',
        locale: 'el',
        id: null,
      } as I18nAddress,
    ],
    x: null,
    y: null,
    isPublic: false,
  },
  property: {
    extras: {
      alarm: '',
      safe: '',
      fireplace: '',
      furnished: '',
      solarWaterHeater: '',
      professionalUse: '',
      penthouse: '',
      doubleFrontage: '',
      withLight: '',
      withAir: '',
      underFloorHeat: '',
      maintained: '',
      screens: '',
      cctv: '',
      disabledAccess: '',
      facade: '',
      corner: '',
      airCondition: '',
      pets: '',
      slopeAmphitheater: '',
      compensation: '',
      waterSupply: '',
      electrification: '',
      enclosure: '',
      drains: '',
      islandBuildable: '',
      marina: '',
      structuredWiring: '',
      falseCeiling: '',
      fireSafety: '',
      kitchen: '',
      doorman: '',
      internalStairs: '',
      foodService: '',
      securityRolls: '',
      cargoRamp: '',
      guarding: '',
      carElevator: '',
      gasStation: '',
      carWash: '',
      waterHeating: '',
      spa: '',
      roofGarden: '',
      facilities: '',
      eventSpace: '',
      intercom: '',
      cellar: '',
      outBar: '',
      staffRooms: '',
      organizedBeach: '',
      meetingRoom: '',
      renovation: '',
      balcony: '',
      tents: '',
      garden: '',
      loft: '',
      storage: '',
      withBuilding: '',
      maintenanceFees: '',
      nonForest: '',
      restaurant: '',
      expandability: '',
      equipped: '',
      commercialPlot: '',
      cargoElevator: '',
    },
    salesPrice: '',
    squareMeters: '',
    estateCode: '',
    constructionYear: '',
    floors: [],
    heat: '',
    heatType: [],
    estateCondition: '',
    door: '',
    energyClass: '',
    frame: '',
    windows: '',
    floor: '',
    access: '',
    direction: '',
    view: '',
    pool: [],
    usage: '',
    plan: '',
    landCondition: [],
    electricity: '',
    bedrooms: '',
    masterBedrooms: '',
    livingRooms: '',
    baths: '',
    wc: '',
    buildFactor: '',
    coverageFactor: '',
    maxAllowedHeight: '',
    plotWidth: '',
    plotLength: '',
    distancePort: '',
    distanceRoadNetwork: '',
    distanceVillage: '',
    distanceTown: '',
    distanceSea: '',
    distanceAirport: '',
    numberOfViews: '',
    beaches: '',
    islandPerimeter: '',
    spaces: '',
    internalStairs: '',
    foodService: '',
    securityRolls: '',
    maxInnerHeight: '',
    industrialDoorHeight: '',
    industrialDoorWidth: '',
    entrances: '',
    offices: '',
    entranceHeight: '',
    slotsCar: '',
    slotsMotorcycle: '',
    slotsBoat: '',
    rooms: '',
    gyms: '',
    facilities: '',
    renovationYear: '',
    balconySqr: '',
    garden: '',
    gardenSqr: '',
    loftSqr: '',
    storageSqr: '',
    maintenanceFeesValue: '',
    parking: '',
    parkingSlots: '',
    withBuildingSqr: '',
    forestCover: '',
    forestCoverSqr: '',
    numberOfRestaurants: '',
    expandabilitySqr: '',
    equipment: '',
    commercialPlot: '',
    plotSqr: '',
    cargoElevatorLoad: '',
  },
  adPhotos: {
    photos: [],
  },
  adAttributes: {
    estateCode: '',
    status: '' as PropertyAdStatus,
    salesPrice: '',
    interest: '',
    agentCInfo: '',
    descriptions: [
      {
        id: null,
        locale: 'el',
        text: '',
      } as I18nText,
    ],
    titles: [
      {
        id: null,
        locale: 'el',
        text: '',
      } as I18nText,
    ],
  },
}

export default function CreatePropertyAd() {
  const { sendPageView } = usePageView({ runOnMount: false })
  const location = useLocation()

  const navigate = useNavigate()

  const currentUser = useAuthStore((state) => state.user)

  const routes = [
    { path: '/property-ads/create' },
    { path: '/property-ads/:propertyAdId' },
    { path: '/my-property-ads/:propertyAdId' },
  ]

  const matchedRoutes = matchRoutes(routes, location)

  const matchEdit =
    !!matchedRoutes?.length &&
    !!matchedRoutes.find(
      (matchedRoute) =>
        matchedRoute?.route?.path === '/property-ads/:propertyAdId' ||
        matchedRoute?.route?.path === '/my-property-ads/:propertyAdId'
    )

  const matchCreate =
    !!matchedRoutes?.length &&
    !!matchedRoutes.find(
      (matchedRoute) => matchedRoute?.route?.path === '/property-ads/create'
    )

  const { propertyAdId } = useParams()

  const [localValidationMessages, setLocalValidationMessages] = React.useState<
    ValidationMessage[]
  >([])
  const [localError, setLocalError] = React.useState<boolean>(false)

  const handleCloseError = () => {
    setLocalError(false)
  }
  const [notFound, setNotFound] = React.useState<boolean>(false)

  const [localPropertyAd, setLocalPropertyAd] = React.useState<
    AdminPropertyAd | undefined
  >()

  const [localEstateAgent, setLocalEstateAgent] = React.useState<
    EstateAgent | undefined
  >()

  const { t } = useTranslation()

  const currentAgentMutation = useMutation(getAgent, {
    onSuccess(data) {
      setLocalEstateAgent(data.estateAgent)
    },
    onError: () => {
      setLocalError(true)
    },
  })

  const {
    handleSubmit,
    control,
    getValues,
    resetField,
    reset,
    trigger,
    setValue,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: DefaultValues,
    mode: 'onChange',
  })

  const [
    currentStep,
    completedSteps,
    setStep,
    resetForm,
    validateStep,
    unsetStepInvalid,
    completeStep,
    uncompleteStep,
    invalidateStep,
    setStepInvalid,
  ] = useCreatePropertyAdStore(
    (state) => [
      state.currentStep,
      state.completedSteps,
      state.setStep,
      state.resetForm,
      state.validateStep,
      state.unsetStepInvalid,
      state.completeStep,
      state.uncompleteStep,
      state.invalidateStep,
      state.setStepInvalid,
    ],
    shallow
  )
  React.useLayoutEffect(() => {
    return () => {
      setStep(0)
      if (!matchEdit) {
        resetForm()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (matchCreate) {
      currentAgentMutation.mutate(currentUser?.estateAgentId)
      sendPageView()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScroll = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }

  React.useEffect(() => {
    handleScroll()
  }, [currentStep])

  React.useEffect(() => {
    handleScroll()
  }, [localValidationMessages])

  const watchCategory = useWatch({
    control,
    name: 'step1.category',
  })
  const watchSubCategory = useWatch({
    control,
    name: 'step1.subCategory',
  })

  const getInitialPropertyStatus = async () => {
    const steps: { path: string; id: string }[] = [
      { path: 'step1', id: 'category' },
      { path: 'location', id: 'location' },
      { path: 'property', id: 'property' },
      { path: 'adPhotos', id: 'adPhotos' },
      { path: 'adAttributes', id: 'adAttributes' },
    ]

    for (const [, step] of steps.entries()) {
      let validStatus
      if (step.path === 'property' && (!watchCategory || !watchSubCategory)) {
        validStatus = false
        continue
      }
      if (step.path === 'adPhotos') {
        continue
      } else {
        validStatus = await trigger(step.path as Path<FormValues>)
      }
      if (validStatus) {
        completeStep(step.id)
        validateStep(step.id)
        unsetStepInvalid(step.id)
      } else {
        uncompleteStep(step.id)
        invalidateStep(step.id)
        setStepInvalid(step.id)
      }
    }
  }

  React.useEffect(() => {
    if (localPropertyAd) {
      getInitialPropertyStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPropertyAd])

  const handleInitState = (propertyAd: AdminPropertyAd) => {
    setLocalEstateAgent(propertyAd?.estateAgent)

    const data: ParsedAdminPropertyAd = parseAdminPropertyAd(propertyAd)

    setLocalValidationMessages(data?.validationMessages ?? [])

    if (matchEdit && data?.estateAgent?.id) {
      currentAgentMutation.mutate(data?.estateAgent?.id)
    }
    if (data?.parsedData) {
      let foundAdressIdx
      foundAdressIdx = data?.properties[0]?.location?.addresses?.findIndex(
        (address) => address?.defaultValue
      )
      if (foundAdressIdx === -1) foundAdressIdx = 0

      const parseStatus = (): PropertyAdStatus | '' => {
        if (
          data?.status ===
            (PROPERTY_AD_STATUS.STATUS_INVALID as PropertyAdStatus) ||
          data?.status ===
            (PROPERTY_AD_STATUS.STATUS_INVALID as PropertyAdStatus)
        ) {
          return '' as PropertyAdStatus
        } else return data?.status as PropertyAdStatus
      }

      reset({
        step1: {
          category: data?.parsedData?.category,
          subCategory: data?.parsedData?.subCategory,
        },
        adAttributes: {
          estateCode: data?.externalId ?? '',
          descriptions: data?.descriptions?.length
            ? data?.descriptions
            : DefaultValues.adAttributes.descriptions,
          titles: data?.titles?.length
            ? data?.titles
            : DefaultValues.adAttributes.titles,
          salesPrice: data?.salesPrice ?? '',
          status: parseStatus() ?? '',
          agentCInfo: data?.agentCInfo?.id ?? '',
          interest: data?.parsedData?.interest ?? '',
        },
        location: {
          addresses: data?.properties[0]?.location?.addresses?.length
            ? data?.properties[0]?.location?.addresses
            : DefaultValues?.location?.addresses,
          x: data?.properties[0]?.location?.xcoordinate,
          y: data?.properties[0]?.location?.ycoordinate,
          searchLocation: data?.properties[0]?.location?.searchLocation,
        },
        adPhotos: {
          photos:
            data?.attachments?.map((item) => {
              return { ...item, type: 'attachment' }
            }) ?? [],
        },
        property: {
          squareMeters: data?.properties[0]?.squareMeters ?? '',
          constructionYear: data?.properties[0]?.constructionYear ?? '',
          buildFactor: data?.properties[0]?.buildFactor ?? '',
          coverageFactor: data?.properties[0]?.coverageFactor ?? '',
          ...{
            ...data?.parsedData,
            ...omit(data?.parsedData, 'category'),
            ...omit(data?.parsedData, 'subCategory'),
          },
          extras: {
            ...data?.parsedData?.extras,
          },
        },
      })
    }
    setLocalPropertyAd(data)
  }

  const propertyAd = usePropertyAd(propertyAdId, {
    enabled: !!propertyAdId && !!matchEdit,
    cacheTime: 0,
    retry: false,
    onSuccess: async (data) => {
      if (data) {
        if (
          data.publishingStatus &&
          [
            PublishingStatusEnum.Pending,
            PublishingStatusEnum.InProgress,
          ].includes(data.publishingStatus)
        ) {
          navigate(
            KeycloakService.isAgent()
              ? '/my-property-ads'
              : KeycloakService.isAdmin()
              ? '/property-ads'
              : '/'
          )
        }
        handleInitState(data)
      }
    },
    onSettled: () => {
      sendPageView()
    },
    onError: (err) => {
      if (err?.response?.status === 404) {
        setNotFound(true)
      } else {
        setLocalError(true)
      }
    },
  })

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    setStep(0)
    if (!matchEdit) {
      resetForm()
      reset({
        step1: DefaultValues.step1,
        adAttributes: DefaultValues.adAttributes,
        location: DefaultValues.location,
        adPhotos: { photos: [] },
        property: DefaultValues.property,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const localSteps: IStep[] = [
    {
      id: 'category',
      label: t('manageEstateAd.steps.estateCategory.title'),
      component: (
        <Step1
          clearErrors={clearErrors}
          trigger={trigger}
          control={control}
          getValues={getValues}
          resetField={resetField}
          reset={reset}
        />
      ),
    },
    {
      id: 'location',
      label: t('manageEstateAd.steps.estateLocation.title'),
      component: (
        <StepPropertyLocation
          trigger={trigger}
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
        />
      ),
    },
    {
      id: 'attributes',
      label: t('manageEstateAd.steps.estateAttributes.title'),
      component: (
        <Step3
          trigger={trigger}
          control={control}
          getValues={getValues}
          resetField={resetField}
          setValue={setValue}
        />
      ),
    },
    {
      id: 'photos',
      label: t('manageEstateAd.steps.estatePhotos.title'),
      component: (
        <StepPhotos
          trigger={trigger}
          control={control}
          getValues={getValues}
          setValue={setValue}
          resetField={resetField}
        />
      ),
    },
    {
      id: 'adAttributes',
      label: t('manageEstateAd.steps.adAttributes.title'),
      component: (
        <StepAdAttributes
          trigger={trigger}
          control={control}
          getValues={getValues}
          resetField={resetField}
          agent={localEstateAgent}
        />
      ),
    },
  ]

  const handleDismissValidation = (validationMessage: ValidationMessage) => {
    setLocalValidationMessages((prev) =>
      prev.filter((item) => item.id !== validationMessage.id)
    )
  }

  return (
    <Box component="form" sx={{ mt: { xs: '10px', md: 0 } }}>
      {propertyAd.isLoading ? (
        <Typography align="center" sx={{ mt: 4 }}>
          <CircularProgress color="secondary" size={30} />
        </Typography>
      ) : null}
      <Grow in={propertyAd.isError && notFound} unmountOnExit>
        <div>
          <NotFound />
        </div>
      </Grow>
      <Grow in={localError} unmountOnExit>
        <Alert
          severity="error"
          onClose={handleCloseError}
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          {t('error.unspecific')}
        </Alert>
      </Grow>
      <Grow
        in={
          propertyAdId
            ? propertyAd.isSuccess && !!propertyAd.data
            : currentAgentMutation.isSuccess && !!currentAgentMutation.data
        }
        unmountOnExit
      >
        <Grid container spacing={3}>
          {localValidationMessages?.length ? (
            <Grid item xs={12}>
              <Box
                sx={{ display: 'grid', gridTemplateRows: '1fr', gap: '10px' }}
              >
                <TransitionGroup component={StyledBox}>
                  {localValidationMessages.map((reason) => (
                    <Grow key={reason.id}>
                      <Alert
                        severity={
                          reason.validationType === 'WARN' ? 'warning' : 'error'
                        }
                        onClose={() => handleDismissValidation(reason)}
                        closeText={
                          t(
                            'manageEstateAd.validation.dismissButton'
                          ) as unknown as string
                        }
                      >
                        <Trans i18nKey={reason.label} />
                      </Alert>
                    </Grow>
                  ))}
                </TransitionGroup>
              </Box>
            </Grid>
          ) : null}
          <Grid item xs={12} md={9}>
            <Box display="grid" gap={2} sx={{}}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  padding: 2,
                  borderRadius: '10px',
                }}
              >
                <Typography sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  {t('manageEstateAd.status')}
                </Typography>
                <LinearProgressWithLabel
                  value={(completedSteps?.length / localSteps.length) * 100}
                />
              </Box>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  padding: 2,
                  display: 'grid',
                  borderRadius: '10px',
                }}
              >
                <Typography fontSize={30} marginBottom={4}>
                  {localSteps[currentStep].label}
                </Typography>
                {localSteps.map((step, index) => (
                  <Collapse
                    key={step.id}
                    in={currentStep === index}
                    unmountOnExit={false}
                  >
                    <Box>{step.component}</Box>
                  </Collapse>
                ))}
                <Controls
                  trigger={trigger}
                  control={control}
                  getValues={getValues}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <VerticalStepper
              IS_IN_EDIT_MODE={matchEdit}
              setLocalPropertyAd={setLocalPropertyAd}
              setLocalValidationMessages={setLocalValidationMessages}
              property={localPropertyAd}
              trigger={trigger}
              control={control}
              getValues={getValues}
              handleSubmit={handleSubmit}
              agent={localEstateAgent}
            />
          </Grid>
        </Grid>
      </Grow>
    </Box>
  )
}
